import { useRef } from 'react';
import styled from '@emotion/styled';
import { MenuItem } from './MenuItem';
import { Jargon, Tooltip, Image, Button } from 'components';
import { IS_DEV } from 'config';
import {
  ClipboardText,
  PuzzlePiece,
  Table,
  ChartBar,
  Receipt,
  Megaphone,
  Certificate,
  Gear,
  DevToLogo,
  Icon,
  TShirt,
  Invoice,
  Factory,
} from '@phosphor-icons/react';
import type { Children as JargonType } from 'components/jargon';
import { useLocation } from 'react-router-dom';
import { delay } from 'lodash';
import { t } from '@lingui/macro';
import DelogueLogo from 'img/delogue-logo.svg';
import { colors } from 'styles/theme/colors';
import { Box } from '@mui/material';
import { useModule, useFeatureFlag, useCheckAccess, useOrgType } from 'features/accessControl';
import useLocalStorageState from 'use-local-storage-state';
import { useUser } from 'features/auth';
import { checkRole } from 'features/accessControl/utils';
import { iframePaths } from 'features/iframe';
import { ExpandableMenuItem } from './ExpandableMenuItem';

export type MenuItem = {
  url: string;
  MenuIcon?: Icon;
  text: JargonType;
  topPadding?: boolean;
  roles?: string[];
  module?: string;
  featureflag?: string;
  accessTo?: 'supplierOnly' | 'designerOnly';
  subItems?: Omit<MenuItem, 'subItems'>[];
};

export type SideBarOpen = 'closed' | 'open' | 'fixed';

// when adding new menu item you need to add it to components/jargon/wordlist.ts as well
// also remember route, page and access control
const menuItems: MenuItem[] = [
  { url: '/taskboard', MenuIcon: ClipboardText, text: 'Taskboard', accessTo: 'designerOnly' },
  { url: '/style', MenuIcon: TShirt, text: 'Styles' },
  { url: '/item', MenuIcon: PuzzlePiece, text: 'Items' },
  {
    url: '/purchase-orders',
    MenuIcon: Invoice,
    text: 'MappingRequest',
    featureflag: 'SupplierModule',
    module: 'Supplier Chain',
  },
  {
    url: '/suppliers',
    MenuIcon: Factory,
    text: 'Suppliers',
    featureflag: 'SupplierModule',
    module: 'Supplier Chain',
    accessTo: 'designerOnly',
    subItems: [
      {
        url: '/direct-suppliers',
        text: 'DirectSuppliers',
        featureflag: 'SupplierModule',
        module: 'Supplier Chain',
      },
      {
        url: '/suppliers',
        text: 'MySubSuppliers',
        featureflag: 'SupplierModule',
        module: 'Supplier Chain',
      },
    ],
  },
  {
    url: '/suppliers',
    MenuIcon: Factory,
    text: 'SubsupplierManagement',
    featureflag: 'SupplierModule',
    module: 'Supplier Chain',
    accessTo: 'supplierOnly',
  },
  { url: '/report', MenuIcon: Table, text: 'Reports' },
  { url: '/analytics', MenuIcon: ChartBar, text: 'Analytics', module: 'BI data' },
  { url: '/order', MenuIcon: Receipt, text: 'Orders', module: 'Orders' },
  { url: '/marketing', MenuIcon: Megaphone, text: 'Marketing', module: 'Marketing' },
  {
    url: '/compliance',
    MenuIcon: Certificate,
    text: 'Compliance',
    featureflag: 'ComplianceDocGenerator',
    module: 'Compliance Document Generator',
    accessTo: 'designerOnly',
  },
  { url: '/admin', MenuIcon: Gear, text: 'Admin', topPadding: true },
];

type SidebarProps = {
  children: React.ReactNode[];
};

export const Page = ({
  item,
  onMenuClick,
  isSideBarOpen,
  marginLeft,
  marginRight,
}: {
  item: MenuItem;
  onMenuClick: () => void;
  isSideBarOpen: SideBarOpen;
  marginLeft?: number;
  marginRight?: number;
}) => {
  const location = useLocation();
  const ff = useFeatureFlag(item.featureflag || '');
  const roleAccess = useCheckAccess(false, item.url);
  const moduleAccess = useModule(item.module || '');
  const isSupplier = useOrgType(2);
  // iframe pages are not available for supplier portal accounts
  if (iframePaths.includes(item.url) && isSupplier) return null;
  // feature flag check
  if (item.featureflag && !ff) return null;
  // RBAC check
  if (!roleAccess) return null;
  // module check, supplier portal accounts have access to all
  if (item.module && !moduleAccess && !isSupplier) return null;
  // only supplier can view
  if (item.accessTo === 'supplierOnly' && !isSupplier) return null;
  // only designer can view
  if (item.accessTo === 'designerOnly' && isSupplier) return null;

  return (
    <MenuItem
      url={item.url}
      MenuIcon={item.MenuIcon}
      topPadding={item.topPadding}
      selected={
        (item.url.startsWith(location.pathname) || location.pathname.startsWith(item.url)) &&
        location.pathname.length > 1
      }
      onClick={onMenuClick}
      extend={isSideBarOpen !== 'closed'}
      marginLeft={marginLeft}
      marginRight={marginRight}
    >
      <Jargon text={item.text} />
    </MenuItem>
  );
};

export const Sidebar = ({ children }: SidebarProps) => {
  const [isSideBarOpen, setIsSideBarOpen] = useLocalStorageState<SideBarOpen>('isSideBarOpen', {
    defaultValue: 'closed',
  });
  const timer = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const hasComplianceModuleAccess = useModule('Compliance');
  const hasComplianceDocGeneratorModuleAccess = useModule('Compliance Document Generator');
  const { data: userData } = useUser();
  const hasOrderModuleAccess =
    useModule('Orders') && checkRole('Order', userData?.loginContext?.roleNames);
  const hasMarketingModuleAccess =
    useModule('Marketing') && checkRole('Marketing', userData?.loginContext?.roleNames);
  const hasBIDataModuleAccess =
    useModule('BI data') && checkRole('Analytics', userData?.loginContext?.roleNames);
  const hasComplianceAdminRoleAccess = checkRole(
    'Compliance Admin',
    userData?.loginContext?.roleNames
  );
  const isComplianceDocGeneratorActive =
    hasComplianceDocGeneratorModuleAccess &&
    (hasComplianceAdminRoleAccess ||
      checkRole('Compliance User', userData?.loginContext?.roleNames));

  const hasSupplierChainDesignerRoles =
    checkRole('Company Admin', userData?.loginContext?.roleNames) ||
    checkRole('Compliance Admin', userData?.loginContext?.roleNames) ||
    checkRole('Compliance User', userData?.loginContext?.roleNames) ||
    checkRole('Supplier Manager', userData?.loginContext?.roleNames);
  const hasSupplierChainSupplierRoles =
    checkRole('Supplier Admin', userData?.loginContext?.roleNames) ||
    checkRole('Supplier User', userData?.loginContext?.roleNames);
  const isDesignerOrganization = userData?.loginContext?.organizationType === 1;
  const isSupplierOrganization = userData?.loginContext?.organizationType === 2;

  const hasSupplierChainAccess =
    (isDesignerOrganization && hasSupplierChainDesignerRoles) ||
    (isSupplierOrganization && hasSupplierChainSupplierRoles);

  const isAdminTabAccessible =
    (hasComplianceAdminRoleAccess && hasComplianceModuleAccess) ||
    checkRole('Company Admin', userData?.loginContext?.roleNames) ||
    checkRole('Supplier Admin', userData?.loginContext?.roleNames) ||
    checkRole('Color Admin', userData?.loginContext?.roleNames) ||
    checkRole('Supplier Manager', userData?.loginContext?.roleNames);
  const onEnter = () => {
    timer.current = setTimeout(() => {
      if (isSideBarOpen === 'closed') {
        setIsSideBarOpen('open');
      }
    }, 1000);
  };

  const onLeave = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    delay(() => {
      if (isSideBarOpen === 'open') {
        setIsSideBarOpen('closed');
      }
    }, 200);
  };

  const onKeep = () => {
    if (isSideBarOpen === 'open' || isSideBarOpen === 'closed') {
      setIsSideBarOpen('fixed');
      return;
    }
    if (isSideBarOpen === 'fixed') {
      setIsSideBarOpen('open');
      return;
    }
  };

  const onMenuClick = () => {
    if (isSideBarOpen === 'open') {
      setIsSideBarOpen('closed');
    }
  };

  const shouldDisplayMenuItem = ({ text }: { text: string }) => {
    if (text === 'Compliance' && !isComplianceDocGeneratorActive) return false;
    if (text === 'Orders' && !hasOrderModuleAccess) return false;
    if (text === 'Marketing' && !hasMarketingModuleAccess) return false;
    if (text === 'Analytics' && !hasBIDataModuleAccess) return false;
    if (text === 'Admin' && !isAdminTabAccessible) return false;
    if ((text === 'Suppliers' || text === 'SubsupplierManagement') && !hasSupplierChainAccess)
      return false;
    if (text === 'MappingRequest' && !hasSupplierChainAccess) return false;
    if (text === 'Items' && isSupplierOrganization) return false;
    // if (
    //   text === 'Taskboard' &&
    //   userData?.loginContext?.organizationType === organizationType.Supplier
    // )
    //   return false;
    return true;
  };

  return (
    <Container>
      <Menu onMouseEnter={onEnter} onMouseLeave={onLeave} open={isSideBarOpen}>
        <LogoAndMenu>
          <LogoCon>
            <Image src={DelogueLogo} alt="Delogue logo" width={40} />
          </LogoCon>
          <MenuItemsContainer>
            {menuItems.filter(shouldDisplayMenuItem).map(item => {
              const key = `${item.url}-${item.text}`;
              if (item.subItems) {
                return (
                  <ExpandableMenuItem
                    key={key}
                    item={item}
                    onMenuClick={onMenuClick}
                    isSideBarOpen={isSideBarOpen}
                  />
                );
              }
              return (
                <Page
                  key={key}
                  item={item}
                  onMenuClick={onMenuClick}
                  isSideBarOpen={isSideBarOpen}
                />
              );
            })}

            {IS_DEV ? (
              <MenuItem
                url="/example/playground"
                MenuIcon={DevToLogo}
                selected={location.pathname === '/example/playground'}
                onClick={() => onMenuClick()}
                extend={isSideBarOpen !== 'closed'}
              >
                <Jargon text="Example" />
              </MenuItem>
            ) : null}
          </MenuItemsContainer>
        </LogoAndMenu>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: isSideBarOpen !== 'closed' ? 'flex-end' : 'center',
          }}
        >
          <Tooltip title={isSideBarOpen === 'fixed' ? t`Collapse sidebar` : t`Lock navigation bar`}>
            <Button variant="link" onClick={() => onKeep()}>
              <SideBarSvg isFixed={isSideBarOpen === 'fixed'} />
            </Button>
          </Tooltip>
        </Box>
      </Menu>
      <TopAndContent fixed={isSideBarOpen === 'fixed'}>
        <Box
          sx={{
            zIndex: 0,
            minHeight: '100vh',
          }}
        >
          {children[0]}
          {children[1]}
        </Box>
      </TopAndContent>
    </Container>
  );
};

const TopAndContent = styled.div<{ fixed: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  margin-left: ${p => (p.fixed ? 312 : 88)}px;
  position: relative;
  overflow-x: auto;
`;

const LogoAndMenu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 32px;
  width: 100%;
`;

const LogoCon = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  position: relative;
  main {
    z-index: 1;
  }
`;

const MenuItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Menu = styled.nav<{ open: 'closed' | 'open' | 'fixed' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: ${p => p.theme.palette.bg.primary};
  height: 100%;
  width: ${p => (p.open !== 'closed' ? '312px' : '88px')};
  transition: width 0.2s ease-in-out;
  justify-content: space-between;
  align-items: flex-end;
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 2;
  overflow-x: hidden;
  overflow-y: auto;
`;

const SideBarSvg = ({ isFixed }: { isFixed: boolean }) => {
  const pathProps = {
    ...(isFixed ? { fill: colors.brand[400] } : { fill: colors.brand[100] }),
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256">
      <path d="M88,48V208H40a8,8,0,0,1-8-8V56a8,8,0,0,1,8-8Z" {...pathProps} />
      <path
        d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H80V200H40ZM216,200H96V56H216V200Z"
        fill={colors.grey[700]}
      />
    </svg>
  );
};
